
import { Control, Controller, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import type { Application } from '@/types/database.types';
import type { PayScheduleType } from '@/types/database/common-types';

export interface PrimaryScheduleProps {
  control: Control<Partial<Application>>;
  register: UseFormRegister<Partial<Application>>;
  watch: UseFormWatch<Partial<Application>>;
  setValue: UseFormSetValue<Partial<Application>>;
}

export const PrimarySchedule = ({ control, register, watch, setValue }: PrimaryScheduleProps) => {
  const scheduleType = watch('pay_schedule_type');
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Primary Payroll Schedule</h3>
      </div>
      
      <div className="space-y-4">
        <Label>Pay Frequency</Label>
        <Controller
          name="pay_schedule_type"
          control={control}
          render={({ field }) => (
            <Select
              value={field.value as string}
              onValueChange={(value) => field.onChange(value as PayScheduleType)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select pay frequency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Weekly">Weekly</SelectItem>
                <SelectItem value="Bi-Weekly">Bi-Weekly (26x/year)</SelectItem>
                <SelectItem value="Semi-Monthly">Semi-Monthly (24x/year)</SelectItem>
                <SelectItem value="Monthly">Monthly</SelectItem>
                <SelectItem value="Quarterly">Quarterly</SelectItem>
                <SelectItem value="Annual">Annual</SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="pay_period_start">Current Period Start</Label>
          <Controller 
            name="pay_period_start"
            control={control}
            render={({ field }) => (
              <DatePicker
                id="pay_period_start"
                date={field.value ? new Date(field.value) : undefined}
                onSelect={(date) => field.onChange(date ? date.toISOString() : undefined)}
                placeholder="Select date"
              />
            )}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="pay_period_end">Current Period End</Label>
          <Controller 
            name="pay_period_end"
            control={control}
            render={({ field }) => (
              <DatePicker
                id="pay_period_end"
                date={field.value ? new Date(field.value) : undefined}
                onSelect={(date) => field.onChange(date ? date.toISOString() : undefined)}
                placeholder="Select date"
              />
            )}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="next_check_date">Next Pay Date for this Schedule</Label>
          <Controller 
            name="next_check_date"
            control={control}
            render={({ field }) => (
              <DatePicker
                id="next_check_date"
                date={field.value ? new Date(field.value) : undefined}
                onSelect={(date) => field.onChange(date ? date.toISOString() : undefined)}
                placeholder="Select date"
              />
            )}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Approximate Number of Employees on this Schedule</Label>
        <div className="flex gap-4">
          <div className="flex items-center gap-2">
            <Label htmlFor="primary_full_time_count" className="text-sm">Full Time:</Label>
            <Input
              id="primary_full_time_count"
              type="number"
              min="0"
              className="w-20"
              placeholder="0"
              {...register('primary_full_time_count')}
            />
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="primary_part_time_count" className="text-sm">Part Time:</Label>
            <Input
              id="primary_part_time_count"
              type="number"
              min="0"
              className="w-20"
              placeholder="0"
              {...register('primary_part_time_count')}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
